/**
 * A reading item: title, then its markdown body (feat-298 spec §5, revised).
 *
 * The body renders through the SHARED reading dialect — `MarkdownPreview` from
 * `curriculum/readingMarkdown.tsx`, the same renderer the curriculum editor's
 * preview uses and the Flutter reader mirrors — so the reviewer sees what the
 * learner sees (single newlines are `<br>`, no syntax the app shows literally).
 * It emits React nodes only: a `<script>` or `<img onerror>` in an authored or
 * TRANSLATED body (translations are untrusted — ADR 0004 D4) is text, links are
 * http(s) only, and there is no image syntax, so the page never fetches an
 * arbitrary remote URL (design Q4). The card has its own `image_urls`.
 *
 * Long bodies are clamped to the old 4-line height with a "Show more / Show
 * less" toggle. The expanded state is CONTROLLED by the caller because in Both
 * mode one toggle — in the English cell — expands both cells of the pair.
 */
import React, { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { ReadingItemSummary } from '../../../services/quizContentAdminService';
import { Column, FieldChip, FieldRun, resolveField, staleRule } from './shared';
import { FOCUS_RING } from './Collapsible';
import { QUIZ_REVIEW_NAV_COPY as COPY } from './navCopy';
import { MarkdownPreview } from '../curriculum/readingMarkdown';
import InlineEditable from './InlineEditable';
import { EditLine } from './editCoordinator';
import { FIELD_LABELS, QUIZ_REVIEW_EDIT_COPY as EDIT } from './editCopy';
import { ItemPendingChip, useLessonId, useReviewEdit } from './reviewEdit';

/** Wrapper classes for the shared renderer inside the card: block spacing only,
 *  so the body keeps the card's `text-sm` / `text-inkMuted` (and therefore the
 *  4-line clamp height below) instead of the editor's `text-ink leading-relaxed`. */
export const READING_BODY_MARKDOWN_CLASS = 'space-y-2';

/** 5rem = 4 lines at `text-sm`'s 20 px leading — the same height `line-clamp-4` had. */
const CLAMP = 'max-h-[5rem] overflow-hidden';
const FADE = '[mask-image:linear-gradient(to_bottom,#000_55%,transparent)]';

export interface ReadingCardProps {
  rd: ReadingItemSummary;
  index: number;
  column: Column;
  fullHeight?: boolean;
  /** Whether the body is unclamped. Controlled: see the header. */
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  /** Render the toggle in THIS cell. Both mode: the English cell only. */
  showToggle: boolean;
  /** True when this cell OR its pair overflows the clamp. */
  overflows: boolean;
  /** Report whether THIS cell's body overflows the clamp. */
  onOverflowChange: (overflows: boolean) => void;
}

const ReadingCard: React.FC<ReadingCardProps> = ({
  rd,
  index,
  column,
  fullHeight,
  expanded,
  onExpandedChange,
  showToggle,
  overflows,
  onOverflowChange,
}) => {
  const imgCount = rd.image_urls?.length ?? rd.image_s3_keys?.length ?? 0;
  const lang = column ?? '';
  const title = resolveField(rd.title, rd.translations, column, 'title');
  const body = resolveField(rd.body, rd.translations, column, 'body');
  const bodyId = useId();
  const bodyRef = useRef<HTMLDivElement | null>(null);
  // feat-299: the English cell is click-to-edit inside the page.
  const api = useReviewEdit();
  const lessonId = useLessonId();
  const editable = api !== null && column === null;
  /** While the body's editor is open: no clamp, no fade, no Show more/less (NOTES §3). */
  const [editingBody, setEditingBody] = useState(false);
  const onBodyOpenChange = useCallback((o: boolean) => setEditingBody(o), []);

  // Overflow is measured in the CLAMPED state only; while expanded the last
  // answer stands, so "Show less" stays available.
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || expanded || editingBody) return undefined;
    const measure = () => onOverflowChange(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [expanded, editingBody, body.text, onOverflowChange]);

  const toggle = showToggle && !editingBody && (overflows || expanded) && (
    <button
      type="button"
      aria-expanded={expanded}
      aria-controls={bodyId}
      onClick={() => onExpandedChange(!expanded)}
      className={`mt-1 inline-flex items-center gap-1 rounded text-xs font-semibold text-reading hover:underline ${FOCUS_RING}`}
    >
      {expanded ? COPY.showLess : COPY.showMore}
      {expanded ? (
        <ChevronUp className="w-3 h-3" aria-hidden />
      ) : (
        <ChevronDown className="w-3 h-3" aria-hidden />
      )}
    </button>
  );
  const chip = body.marker !== 'none' && <FieldChip marker={body.marker} language={lang} />;

  const bodyBlock = (source: string) => (
    <div
      id={bodyId}
      ref={bodyRef}
      lang={body.lang ?? undefined}
      data-testid="reading-body"
      className={`text-sm text-inkMuted break-words${staleRule(body)}${
        body.marker === 'stale' ? ' pl-2' : ''
      }${expanded ? '' : ` ${CLAMP}${overflows ? ` ${FADE}` : ''}`}`}
    >
      <MarkdownPreview source={source} emptyText={null} className={READING_BODY_MARKDOWN_CLASS} />
    </div>
  );

  return (
    <div
      className={`border-l-4 border-reading pl-3 py-1 bg-reading-light/40 rounded-r${
        fullHeight ? ' h-full' : ''
      }`}
    >
      {editable ? (
        <EditLine className="font-semibold text-ink mb-1" prefix={`📖 R${index}.`}>
          <InlineEditable
            value={rd.title ?? ''}
            field="title"
            label={FIELD_LABELS.readingTitle()}
            required
            fallback={COPY.untitledReading}
            onSave={(next) => api.saveReading(lessonId, rd.id, { title: next })}
          />{' '}
          <span className="text-xs font-normal text-reading">
            · no XP
            {imgCount > 0 ? ` · ${imgCount} image${imgCount === 1 ? '' : 's'}` : ''}
          </span>
          <ItemPendingChip entity="reading_item" id={rd.id} />
        </EditLine>
      ) : (
        <div className="font-semibold text-ink mb-1">
          📖 R{index}. {title.text ? <FieldRun f={title} language={lang} /> : COPY.untitledReading}{' '}
          <span className="text-xs font-normal text-reading">
            · no XP
            {imgCount > 0 ? ` · ${imgCount} image${imgCount === 1 ? '' : 's'}` : ''}
          </span>
        </div>
      )}
      {(body.text || editable) && (
        <>
          {editable ? (
            <div className="text-sm text-inkMuted">
              <InlineEditable
                value={rd.body ?? ''}
                field="body"
                label={FIELD_LABELS.readingBody()}
                display="block"
                multiline
                required
                placeholder={EDIT.placeholderReadingBody}
                render={bodyBlock}
                editorNote={EDIT.markdownHint}
                onOpenChange={onBodyOpenChange}
                onSave={(next) => api.saveReading(lessonId, rd.id, { body: next })}
              />
            </div>
          ) : (
            bodyBlock(body.text)
          )}
          {(chip || toggle) && (
            // The chip cannot trail a block element inline, so it leads the
            // footer row, beside the toggle (NOTES §10).
            <div className="flex items-center gap-2">
              {chip}
              {toggle}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReadingCard;
